import { GENERATION_TIMEOUT_SECONDS } from "../constants";

const STORAGE_KEY = "instant3d:pending-generation";

/**
 * Persisted record of an in-flight image-to-3D generation so tracking survives
 * a full page reload (e.g. navigating to /profile and back). The `flow` tag is
 * load-bearing: `pendingJob` in App is shared with the character-generator flow,
 * whose Supabase jobs must never be restored as Firestore jobs.
 */
export interface PendingGeneration {
  flow: "imageTo3D";
  generationId: string;
  /** Epoch ms when the generation was started. */
  startedAt: number;
  /** Remote (Azure) image URLs — object URLs do not survive a reload. */
  uploadedUrls: string[];
}

export function savePendingGeneration(pending: PendingGeneration): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage unavailable (private mode/quota) — persistence is best-effort.
  }
}

export function loadPendingGeneration(): PendingGeneration | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const isValid =
      parsed &&
      parsed.flow === "imageTo3D" &&
      typeof parsed.generationId === "string" &&
      typeof parsed.startedAt === "number" &&
      Array.isArray(parsed.uploadedUrls) &&
      parsed.uploadedUrls.every((url: unknown) => typeof url === "string");
    if (!isValid) {
      clearPendingGeneration();
      return null;
    }
    if (Date.now() - parsed.startedAt > GENERATION_TIMEOUT_SECONDS * 1000) {
      clearPendingGeneration();
      return null;
    }
    return parsed as PendingGeneration;
  } catch {
    return null;
  }
}

export function clearPendingGeneration(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — nothing to clean up if storage is unavailable.
  }
}
